"use client";

import React, { useState } from "react";
import { 
  X, 
  Stethoscope, 
  Calendar, 
  UserCheck, 
  Activity, 
  CreditCard, 
  FileText,
  AlertCircle
} from "lucide-react";
import { DentalPatientLead } from "@/lib/demos/dentalDb";
import { DENTAL_DOCTORS, DENTAL_TREATMENTS } from "@/lib/demos/dentalData";

interface DentalStageModalProps {
  patientLead: DentalPatientLead;
  targetStage: DentalPatientLead["stage"];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (options: any) => void;
}

export default function DentalStageModal({
  patientLead,
  targetStage,
  isOpen,
  onClose,
  onConfirm,
}: DentalStageModalProps) {
  // Stage 2: 3D Scan Scheduled
  const [scanDate, setScanDate] = useState(
    patientLead.scan_date || "Tomorrow at 11:30 AM (Suite 201)"
  );
  const [attendingDoctor, setAttendingDoctor] = useState(
    patientLead.preferred_doctor || "Dr. Aanya Singhania (Chief Prosthodontist)"
  );
  const [clinicalSuite, setClinicalSuite] = useState("Executive Operatory Suite 201");

  // Stage 3: Treatment Plan Presented
  const [treatmentPlanSummary, setTreatmentPlanSummary] = useState(
    "Digital Smile Design 3D wax-up finalized with master ceramist review."
  );
  const [estimatedCost, setEstimatedCost] = useState(
    patientLead.estimated_cost || 240000
  );

  // Stage 5: Procedure Completed (Won)
  const [paymentMethod, setPaymentMethod] = useState("HDFC Infinia POS Verified");
  const [warrantyPeriod, setWarrantyPeriod] = useState("10-Year Clinical Porcelain Guarantee");

  // Stage 6: Recall Scheduled
  const [recallNotes, setRecallNotes] = useState("6-month ultrasonic hygiene recall scheduled");

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm({
      stage: targetStage,
      scan_date: scanDate,
      preferred_doctor: attendingDoctor,
      clinicalSuite,
      treatmentPlanSummary,
      estimated_cost: Number(estimatedCost),
      paymentMethod,
      warrantyPeriod,
      clinical_notes: targetStage === "6-Month Recall Scheduled" ? recallNotes : undefined,
    });
  };

  const sym = patientLead.currency === "USD" ? "$" : "₹";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-slate-950 border border-slate-800 rounded-3xl max-w-lg w-full p-6 sm:p-7 relative shadow-2xl text-slate-100 space-y-5">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-slate-400 hover:text-white p-1.5 rounded-full hover:bg-slate-900 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
          <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/30 flex items-center justify-center text-teal-400 shrink-0">
            <Stethoscope className="w-5 h-5" />
          </div>
          <div>
            <span className="text-[10px] font-bold text-teal-400 uppercase tracking-wider block">
              Clinical Stage Transition Automation
            </span>
            <h3 className="text-base sm:text-lg font-black text-white">
              Advance Patient to {targetStage}
            </h3>
            <p className="text-xs text-slate-400">
              Patient: <span className="text-white font-semibold">{patientLead.patient_name}</span> ({patientLead.email})
            </p>
          </div>
        </div>

        {/* STAGE 2: PRELIMINARY 3D SCAN SCHEDULED */}
        {targetStage === "Preliminary 3D Scan Scheduled" && (
          <div className="space-y-4 text-xs">
            <div className="p-3 rounded-xl bg-teal-500/10 border border-teal-500/20 text-teal-300">
              Advancing to this stage will automatically generate and email the official <strong>Clinical 3D Scan &amp; Appointment Pass PDF</strong> with operatory allocation to the patient.
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-teal-400" />
                <span>Appointment Date &amp; Time *</span>
              </label>
              <input
                type="text"
                value={scanDate}
                onChange={(e) => setScanDate(e.target.value)}
                placeholder="e.g. Tomorrow at 11:30 AM (Suite 201)"
                className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-teal-500"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                <UserCheck className="w-3.5 h-3.5 text-teal-400" />
                <span>Attending Specialist Doctor *</span>
              </label>
              <select
                value={attendingDoctor}
                onChange={(e) => setAttendingDoctor(e.target.value)}
                className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-teal-500"
              >
                {DENTAL_DOCTORS.map((doc) => (
                  <option key={doc.id} value={`${doc.name} (${doc.title.split("&")[0].trim()})`}>
                    {doc.name} — {doc.title.split("&")[0].trim()}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                Operatory Suite
              </label>
              <input
                type="text"
                value={clinicalSuite}
                onChange={(e) => setClinicalSuite(e.target.value)}
                className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-teal-500"
              />
            </div>
          </div>
        )}

        {/* STAGE 3: TREATMENT PLAN PRESENTED */}
        {targetStage === "Treatment Plan Presented" && (
          <div className="space-y-4 text-xs">
            <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-300">
              Provide clinical findings and estimated investment to email the formal treatment proposal and 3D simulation to the patient.
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                Treatment Plan Summary *
              </label>
              <textarea
                rows={2}
                value={treatmentPlanSummary}
                onChange={(e) => setTreatmentPlanSummary(e.target.value)}
                className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-cyan-500"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                Estimated Clinical Fee ({patientLead.currency}) *
              </label>
              <input
                type="number"
                value={estimatedCost}
                onChange={(e) => setEstimatedCost(Number(e.target.value))}
                className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-cyan-500"
              />
            </div>
          </div>
        )}

        {/* STAGE 5: PROCEDURE COMPLETED (WON) */}
        {targetStage === "Procedure Completed (Won)" && (
          <div className="space-y-3.5 text-xs">
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300">
              Completing this clinical case will automatically generate and email the official <strong>Clinical Procedure Receipt &amp; Tax Invoice PDF</strong> with warranty certificate.
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-300 uppercase tracking-wider">
                  Total Paid ({sym}) *
                </label>
                <input
                  type="number"
                  value={estimatedCost}
                  onChange={(e) => setEstimatedCost(Number(e.target.value))}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-300 uppercase tracking-wider">
                  Payment Mode *
                </label>
                <input
                  type="text"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div className="space-y-1 col-span-2">
                <label className="text-[10px] font-bold text-slate-300 uppercase tracking-wider">
                  Clinical Warranty Issued
                </label>
                <input
                  type="text"
                  value={warrantyPeriod}
                  onChange={(e) => setWarrantyPeriod(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* STAGE 6: 6-MONTH RECALL SCHEDULED */}
        {targetStage === "6-Month Recall Scheduled" && (
          <div className="space-y-3 text-xs">
            <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 text-slate-400">
              Queue patient into the 6-month preventive hygiene recall system.
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                Recall Notes &amp; Prophylaxis Schedule *
              </label>
              <input
                type="text"
                value={recallNotes}
                onChange={(e) => setRecallNotes(e.target.value)}
                className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3.5 py-2 text-xs text-white focus:outline-none focus:border-slate-500"
              />
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="pt-3 border-t border-slate-800 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white transition-colors"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleConfirm}
            className="px-6 py-2 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-xs transition-colors shadow-md shadow-teal-500/20 flex items-center gap-1.5"
          >
            <span>Confirm &amp; Dispatch Email</span>
          </button>
        </div>
      </div>
    </div>
  );
}
